/*
 * Welcome popup
 * ---------------------------------------------------------------------------
 * Opens the 25% ticket in sections/popups.liquid once a visitor has spent
 * `delay` seconds on the store.
 *
 * The seconds are time on the STORE, not on one page. theme.min.js restarted
 * its own timer on every page load, so a shopper who went ad -> collection ->
 * product inside the delay never reached it on any page. Here the visible time
 * is kept in sessionStorage and each page carries on from where the last one
 * stopped. It only counts while the tab is on screen.
 *
 * The rest is about not getting in the way:
 *  - it waits while the cart drawer, the phone menu, search or another modal
 *    is open, and opens the moment they close;
 *  - once it has opened, it never opens on its own again for that visitor
 *    (`showOnlyOnce`) or that visit;
 *  - anyone who joined, from here or from the footer ticket, never sees it
 *    again;
 *  - closing it without joining leaves a corner tab for the rest of the visit.
 *    The tab reopens the popup, and its x or a swipe down hides it.
 *
 * After the form posts, Shopify sends the shopper back to #newsletter-popup.
 * That hash reopens the popup, now drawn with the code (or with the error).
 */
(function () {
  'use strict';

  var POPUP = '[data-welcome-popup]';
  var TEASER = '[data-welcome-teaser]';
  var HASH = '#newsletter-popup';

  /* sessionStorage: ms counted so far this visit, or -1 once it has opened. */
  var ELAPSED_KEY = 'welcomePopupElapsed';
  /* sessionStorage: "shown" while the corner tab is up, "hidden" once swiped. */
  var TEASER_KEY = 'welcomePopupTeaser';
  /* localStorage: "shown", "dismissed" or "joined". A new key on purpose: the
     old popup's "themePopup" was set by a popup that offered nothing, and the
     visitors who closed that one have never been offered the 25%. */
  var STATUS_KEY = 'welcomePopup';

  /* Anything open that the popup must not cover. shopify-chat[open] is the
     Shopify Inbox app's chat window: a shopper mid-conversation is not
     interrupted. */
  var BUSY = '#mini-cart[aria-hidden="false"], #mobile-menu[aria-hidden="false"], .search-bar.is-expanded, .modal[aria-hidden="false"], shopify-chat[open]';

  /* The most page-load time credited to the clock, so a stalled load does not
     count as a minute on the store. */
  var LOAD_CREDIT_MAX = 10000;
  var BUSY_RETRY = 1000;

  /* How far down the tab has to travel before letting go hides it. */
  var SWIPE_DISTANCE = 36;
  var TAP_SLOP = 8;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  function read(storage, key) {
    try { return window[storage].getItem(key); } catch (e) { return null; }
  }

  function write(storage, key, value) {
    try { window[storage].setItem(key, value); } catch (e) { /* private mode */ }
  }

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function isBusy() {
    if (window.theme && window.theme.isExitPopupOpen) { return true; }
    return !!document.querySelector(BUSY);
  }

  function WelcomePopup(element) {
    this.element = element;
    this.dialog = element.querySelector('[role="dialog"]');
    this.teaser = document.querySelector(TEASER);
    this.state = (element.querySelector('[data-welcome-state]') || { getAttribute: function () { return 'invite'; } }).getAttribute('data-welcome-state');

    try {
      this.options = JSON.parse(element.getAttribute('data-popup-settings')) || {};
    } catch (e) {
      this.options = {};
    }

    this.delay = Math.max(0, Number(this.options.delay) || 0) * 1000;
    this.isOpen = false;
    this.since = null;
    this.timer = null;

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onVisibility = this._onVisibility.bind(this);
    this._onPageHide = this._onPageHide.bind(this);
    this._onPageShow = this._onPageShow.bind(this);
    this._onClick = this._onClick.bind(this);

    element.addEventListener('click', this._onClick);
    if (this.teaser) { this._bindTeaser(); }

    /* Joined anywhere on the page -- here or the footer ticket, which share
       Shopify's one customer-form state -- is joined for good. */
    if (this.state === 'claimed') { write('localStorage', STATUS_KEY, 'joined'); }

    if (designMode()) { return; }

    if (window.location.hash === HASH) {
      this.open();
      this._clearHash();
      return;
    }

    this._restoreTeaser();

    if (this._due()) {
      /* The page was loading before this deferred script ran, and the shopper
         was on the store for that time too. Without it, each page's load time
         was missing from the 40 seconds: the popup opened about 43 seconds in
         on a fast connection, later on a slow phone or after several pages. */
      this.elapsed = (Number(read('sessionStorage', ELAPSED_KEY)) || 0) + this._loadTime();
      document.addEventListener('visibilitychange', this._onVisibility);
      window.addEventListener('pagehide', this._onPageHide);
      window.addEventListener('pageshow', this._onPageShow);
      this._start();
    }
  }

  /* ---------------------------------------------------------- the clock ---- */

  /* Should this page still be counting towards an automatic open? */
  WelcomePopup.prototype._due = function () {
    var status = read('localStorage', STATUS_KEY);
    if (status === 'joined') { return false; }
    if (this.options.showOnlyOnce && status) { return false; }
    return read('sessionStorage', ELAPSED_KEY) !== '-1';
  };

  /* Time since this page's navigation started, if the page is on screen. A
     page that loaded in a background tab is credited nothing. */
  WelcomePopup.prototype._loadTime = function () {
    if (document.visibilityState === 'hidden' || !window.performance || !window.performance.now) { return 0; }
    return Math.min(Math.max(0, window.performance.now()), LOAD_CREDIT_MAX);
  };

  WelcomePopup.prototype._start = function () {
    if (this.since !== null || document.visibilityState === 'hidden') { return; }
    this.since = Date.now();
    this.timer = setTimeout(this._fire.bind(this), Math.max(0, this.delay - this.elapsed));
  };

  WelcomePopup.prototype._stop = function () {
    if (this.since === null) { return; }
    clearTimeout(this.timer);
    this.elapsed += Date.now() - this.since;
    this.since = null;
    write('sessionStorage', ELAPSED_KEY, String(Math.round(this.elapsed)));
  };

  WelcomePopup.prototype._fire = function () {
    if (document.visibilityState === 'hidden') { return; }
    if (isBusy() || this.isOpen) {
      this.timer = setTimeout(this._fire.bind(this), BUSY_RETRY);
      return;
    }
    this._finishClock();
    this.open();
  };

  WelcomePopup.prototype._finishClock = function () {
    clearTimeout(this.timer);
    this.since = null;
    write('sessionStorage', ELAPSED_KEY, '-1');
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('pagehide', this._onPageHide);
    window.removeEventListener('pageshow', this._onPageShow);
  };

  WelcomePopup.prototype._onVisibility = function () {
    if (document.visibilityState === 'hidden') { this._stop(); } else { this._start(); }
  };

  WelcomePopup.prototype._onPageHide = function () { this._stop(); };

  /* Back to a page from the back/forward cache: carry on from what was saved,
     which may be ahead of this page's copy if other pages counted since. */
  WelcomePopup.prototype._onPageShow = function (event) {
    if (!event.persisted) { return; }
    if (!this._due()) { this._finishClock(); return; }
    this.elapsed = Number(read('sessionStorage', ELAPSED_KEY)) || 0;
    this._start();
  };

  /* ---------------------------------------------------- open and close ---- */

  WelcomePopup.prototype.open = function () {
    if (this.isOpen) { return; }
    this.isOpen = true;
    this.lastFocus = document.activeElement;

    if (!read('localStorage', STATUS_KEY)) { write('localStorage', STATUS_KEY, 'shown'); }
    if (this.since !== null || this.timer) { this._finishClock(); }

    this._showTeaser(false);
    this.element.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('welcome-popup-open');
    window.theme = window.theme || {};
    window.theme.isNewsletterPopupOpen = true;
    document.addEventListener('keydown', this._onKeyDown);

    /* The dialog itself, not the field: focusing the field on a phone throws
       the keyboard up over the offer before it has been read. */
    if (this.dialog) { this.dialog.focus(); }
  };

  /* `quiet` is the theme editor deselecting the block: no bookkeeping. */
  WelcomePopup.prototype.close = function (quiet) {
    if (!this.isOpen) { return; }
    this.isOpen = false;

    this.element.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('welcome-popup-open');
    if (window.theme) { window.theme.isNewsletterPopupOpen = false; }
    document.removeEventListener('keydown', this._onKeyDown);

    if (!quiet && this.state !== 'claimed' && read('localStorage', STATUS_KEY) !== 'joined') {
      write('localStorage', STATUS_KEY, 'dismissed');
      if (this.options.showTeaser && read('sessionStorage', TEASER_KEY) !== 'hidden') {
        write('sessionStorage', TEASER_KEY, 'shown');
        this._showTeaser(true);
      }
    }

    if (this.lastFocus && this.lastFocus.focus && document.contains(this.lastFocus)) {
      this.lastFocus.focus();
    }
  };

  WelcomePopup.prototype._onClick = function (event) {
    if (event.target === this.element) {
      /* The backdrop: everything outside the dialog. */
      this.close();
      return;
    }
    var trigger = event.target.closest('[data-action="close-welcome-popup"], [data-action="claim-discount"]');
    if (!trigger) { return; }
    /* The claim link is assets/cart-discounts.js's to handle. It opens the cart
       drawer, which should not be under this. */
    this.close();
  };

  WelcomePopup.prototype._onKeyDown = function (event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.dialog) { return; }

    var items = Array.prototype.filter.call(this.dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) { return; }

    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* A reload must not reopen the popup, and the address bar should not keep
     a hash the shopper never typed. */
  WelcomePopup.prototype._clearHash = function () {
    try {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    } catch (e) { /* leave it */ }
  };

  /* ---------------------------------------------------- the corner tab ---- */

  WelcomePopup.prototype._restoreTeaser = function () {
    if (!this.options.showTeaser) { return; }
    if (read('localStorage', STATUS_KEY) === 'joined') { return; }
    if (read('sessionStorage', TEASER_KEY) === 'shown') { this._showTeaser(true); }
  };

  WelcomePopup.prototype._showTeaser = function (show) {
    if (!this.teaser) { return; }
    this.teaser.classList.remove('is-swiped', 'is-dragging');
    this.teaser.style.transform = '';
    this.teaser.style.opacity = '';
    if (show) { this.teaser.removeAttribute('hidden'); } else { this.teaser.setAttribute('hidden', ''); }
  };

  WelcomePopup.prototype._hideTeaser = function (swiped) {
    write('sessionStorage', TEASER_KEY, 'hidden');
    if (!swiped || !this.teaser) {
      this._showTeaser(false);
      return;
    }

    var teaser = this.teaser;
    var self = this;
    var done = function () {
      teaser.removeEventListener('transitionend', done);
      clearTimeout(fallback);
      if (read('sessionStorage', TEASER_KEY) === 'hidden') { self._showTeaser(false); }
    };
    /* transitionend never comes with reduced motion, so a timer backs it up. */
    var fallback = setTimeout(done, 300);
    teaser.addEventListener('transitionend', done);

    teaser.classList.remove('is-dragging');
    teaser.style.transform = '';
    teaser.style.opacity = '';
    teaser.classList.add('is-swiped');
  };

  WelcomePopup.prototype._bindTeaser = function () {
    var self = this;
    var teaser = this.teaser;
    var drag = null;
    var suppressClick = false;

    teaser.addEventListener('click', function (event) {
      /* A swipe that ends over a button is not a tap on it. */
      if (suppressClick) {
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      var trigger = event.target.closest('[data-action]');
      if (!trigger) { return; }
      var action = trigger.getAttribute('data-action');
      if (action === 'open-welcome-popup') {
        self.open();
      } else if (action === 'hide-welcome-teaser') {
        self._hideTeaser(false);
      }
    });

    teaser.addEventListener('pointerdown', function (event) {
      if (event.button !== undefined && event.button !== 0) { return; }
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dy: 0, moved: false };
      suppressClick = false;
    });

    teaser.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) { return; }
      var dx = event.clientX - drag.x;
      var dy = event.clientY - drag.y;

      if (!drag.moved) {
        if (Math.abs(dx) < TAP_SLOP && Math.abs(dy) < TAP_SLOP) { return; }
        drag.moved = true;
        teaser.classList.add('is-dragging');
        if (teaser.setPointerCapture) {
          try { teaser.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
        }
      }

      /* Down only. Pulling up resists and stays put. */
      drag.dy = Math.max(0, dy);
      teaser.style.transform = 'translateY(' + drag.dy + 'px)';
      teaser.style.opacity = String(Math.max(0.2, 1 - drag.dy / 120));
    });

    var release = function (event) {
      if (!drag || event.pointerId !== drag.id) { return; }
      var moved = drag.moved;
      var dy = drag.dy;
      drag = null;
      if (!moved) { return; }

      suppressClick = true;
      /* The click that follows a drag never comes on some browsers; do not let
         the flag eat the next real tap. */
      setTimeout(function () { suppressClick = false; }, 400);

      if (event.type !== 'pointercancel' && dy >= SWIPE_DISTANCE) {
        self._hideTeaser(true);
      } else {
        teaser.classList.remove('is-dragging');
        teaser.style.transform = '';
        teaser.style.opacity = '';
      }
    };

    teaser.addEventListener('pointerup', release);
    teaser.addEventListener('pointercancel', release);
  };

  WelcomePopup.prototype.destroy = function () {
    this.close(true);
    this._finishClockListeners();
    this.element.removeEventListener('click', this._onClick);
  };

  WelcomePopup.prototype._finishClockListeners = function () {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('pagehide', this._onPageHide);
    window.removeEventListener('pageshow', this._onPageShow);
  };

  /* ------------------------------------------------------------- boot ---- */

  var instance = null;

  function boot() {
    var element = document.querySelector(POPUP);
    if (!element || element.__welcomePopup) { return; }
    element.__welcomePopup = true;
    instance = new WelcomePopup(element);
    window.theme = window.theme || {};
    window.theme.welcomePopup = instance;
  }

  /* The theme editor: selecting the block opens it, so it can be edited. */
  document.addEventListener('shopify:block:select', function (event) {
    if (instance && event.target === instance.element) { instance.open(); }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    if (instance && event.target === instance.element) { instance.close(true); }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (instance && event.target.contains(instance.element)) {
      instance.destroy();
      instance = null;
    }
  });

  document.addEventListener('shopify:section:load', boot);

  /* Joined from the footer without a reload (assets/customer-form.js). The
     page used to come back claimed, which is how the constructor above learnt
     about it; now nothing comes back, so the footer says so instead. Stop the
     clock and take the 25% tab down -- this shopper has the code on screen. */
  document.addEventListener('customer-form:posted', function (event) {
    if (!event.detail || event.detail.state !== 'posted') { return; }
    write('localStorage', STATUS_KEY, 'joined');
    if (instance) {
      instance._finishClockListeners();
      instance._showTeaser(false);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
